import { inject, Injectable } from '@angular/core';
import { LoggerService } from '../../../../shared/services/logger.service';
import { ToastService } from '../toast.service';
import { TranslateService } from '@ngx-translate/core';
import { Capacitor } from '@capacitor/core';
import { AuthService } from '../auth/auth.service';
import { DefaultSystemBrowserOptions, InAppBrowser } from '@capacitor/inappbrowser';

declare global {
  interface JivoApi {
    open: () => { result: 'success' | 'fail' };
    setContactInfo: (contactInfo: { name: string; email: string; phone: string; description: string }) => void;
  }

  interface Window {
    jivo_init?: () => void;
    jivo_destroy?: () => void;
    jivo_api: JivoApi;
  }
}

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  loggerService = inject(LoggerService);
  toastService = inject(ToastService);
  translateService = inject(TranslateService);
  authService = inject(AuthService);

  isFirstRun = true;
  chatIsInited = false;
  chatIsOpen = false;

  private observer: MutationObserver | null = null;

  constructor() {}

  onJivoChatClosed() {
    window.jivo_destroy?.();
    this.chatIsInited = false;
    this.chatIsOpen = false;
  }

  onJivoChatOpened() {
    this.chatIsOpen = true;
  }

  onJivoChatLoaded() {
    if (this.isFirstRun) {
      window.jivo_destroy?.();
      this.isFirstRun = false;

      this.monitorHtmlClass();

      return;
    }

    // Once chat is inited, open it (step 2)
    this.openChatDialog();
  }

  onJivoChatDestroyed() {
    this.chatIsInited = false;
    this.chatIsOpen = false;
  }

  openChat() {
    if (this.chatIsOpen) {
      return;
    }

    if (Capacitor.isNativePlatform()) {
      this.showChatInBrowser();
      return;
    }

    // If chat is not inited, init it (step 1)
    if (!this.chatIsInited) {
      if (!window.jivo_init) {
        this.loggerService.logException('JivoChat was not loaded, jivo_init method is not available', 'JivoChat');
        console.log('JivoChat was not loaded, jivo_init method is not available');
        this.toastService.showToast(this.translateService.instant('HELP.CHAT_NOT_LOADED'));
        return;
      }

      window.jivo_init();
    }
  }

  private openChatDialog() {
    const jivo_api = window.jivo_api;

    if (!jivo_api) {
      this.loggerService.logException('JivoChat was loaded, but jivo_api is not available', 'JivoChat');
      console.log('JivoChat was loaded, but jivo_api is not available');
      this.toastService.showToast(this.translateService.instant('HELP.CHAT_NOT_LOADED'));
      return;
    }

    const apiResult = jivo_api.open();

    if (apiResult.result === 'fail') {
      this.loggerService.logException('JivoChat failed to open', 'JivoChat', apiResult);
      console.log('JivoChat failed to open');
      this.toastService.showToast(this.translateService.instant('HELP.CHAT_NOT_LOADED'));
      return;
    } else {
      console.log('JivoChat opened successfully');
    }

    this.setContactInfo();
  }

  private setContactInfo() {
    const jivo_api = window.jivo_api;

    const user = this.authService.startPrintUser();

    if (!user) {
      return;
    }

    jivo_api?.setContactInfo({
      name: user.displayName ?? '',
      email: user.email ?? '',
      phone: user.mobilePhone ?? '',
      description: '',
    });
  }

  private async showChatInBrowser() {
    await InAppBrowser.openInSystemBrowser({
      url: 'https://jivo.chat/wf1ciAIjkm', // 'https://startprint.net?openChat=true',
      options: DefaultSystemBrowserOptions,
    });
  }

  private monitorHtmlClass() {
    if (this.observer) return; // Already monitoring

    this.observer = new MutationObserver(() => {
      const html = document.documentElement;

      if (html.classList.contains('jivo-site-full-block')) {
        html.classList.remove('jivo-site-full-block');
      }
    });

    this.observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }
}
